import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, unwrap, type Schemas } from '../../api/client';
import { useNotify, errorMessage } from '../../lib/notify';
import { applyFieldErrors, errorCode } from '../admins/formErrors';

export type Plan = Schemas['SubscriptionPlan'];
export type PlanInterval = Plan['interval'];

export const INTERVAL_LABELS: Record<PlanInterval, string> = {
  MONTH: 'Monthly',
  YEAR: 'Yearly',
  CUSTOM: 'Custom',
};
const DEFAULT_DAYS: Record<PlanInterval, number> = { MONTH: 30, YEAR: 365, CUSTOM: 90 };

const days = z
  .string()
  .trim()
  .regex(/^\d+$/, 'Enter whole days')
  .refine((v) => Number(v) >= 1 && Number(v) <= 3660, 'Between 1 and 3660 days');
const maxUsers = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 1000), {
    message: 'Between 1 and 1000, or empty for unlimited',
  });
const price = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{1,15}(\.\d{1,4})?$/.test(v), 'A number with up to 4 decimals');

const createSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Enter a code')
    .regex(/^[a-z0-9][a-z0-9_-]{0,49}$/, 'Lowercase letters, digits, - or _ (max 50)'),
  name: z.string().trim().min(1, 'Enter a name').max(200),
  interval: z.enum(['MONTH', 'YEAR', 'CUSTOM']),
  durationDays: days,
  maxUsers,
  price,
  currency: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/, 'Three capital letters, e.g. PKR'),
  isActive: z.boolean(),
});
type Values = z.infer<typeof createSchema>;
const FIELDS = [
  'code',
  'name',
  'interval',
  'durationDays',
  'maxUsers',
  'price',
  'currency',
] as const;

/** Create (no `plan`) or edit a subscription plan. Code, interval and currency are fixed once created. */
export function PlanDialog({
  open,
  plan,
  onClose,
}: {
  open: boolean;
  plan?: Plan | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {open && <PlanForm plan={plan ?? null} onClose={onClose} />}
    </Dialog>
  );
}

function PlanForm({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const editing = plan !== null;
  const notify = useNotify();
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Values>({
    resolver: zodResolver(createSchema),
    mode: 'onBlur',
    defaultValues: plan
      ? {
          code: plan.code,
          name: plan.name,
          interval: plan.interval,
          durationDays: String(plan.durationDays),
          maxUsers: plan.maxUsers == null ? '' : String(plan.maxUsers),
          price: plan.price ?? '',
          currency: plan.currency,
          isActive: plan.isActive,
        }
      : {
          code: '',
          name: '',
          interval: 'MONTH',
          durationDays: '30',
          maxUsers: '',
          price: '',
          currency: 'PKR',
          isActive: true,
        },
  });

  const save = useMutation({
    mutationFn: async (v: Values) => {
      if (plan) {
        return unwrap(
          api.PATCH('/platform/plans/{id}', {
            params: { path: { id: plan.id } },
            body: {
              name: v.name.trim(),
              durationDays: Number(v.durationDays),
              maxUsers: v.maxUsers.trim() === '' ? null : Number(v.maxUsers),
              price: v.price.trim() === '' ? null : v.price.trim(),
              isActive: v.isActive,
              version: plan.version,
            },
          }),
        );
      }
      return unwrap(
        api.POST('/platform/plans', {
          body: {
            code: v.code.trim(),
            name: v.name.trim(),
            interval: v.interval,
            durationDays: Number(v.durationDays),
            ...(v.maxUsers.trim() ? { maxUsers: Number(v.maxUsers) } : {}),
            ...(v.price.trim() ? { price: v.price.trim() } : {}),
            currency: v.currency.trim(),
          },
        }),
      );
    },
  });

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      const saved = await save.mutateAsync(v);
      await qc.invalidateQueries({ queryKey: ['plans'] });
      notify.success(editing ? `Saved ${saved.name}` : `Created plan ${saved.name}`);
      onClose();
    } catch (e) {
      const code = errorCode(e);
      if (code === 'PLAN_CODE_TAKEN') {
        setError('code', { type: 'server', message: 'Another plan already uses this code.' });
      } else if (code === 'VERSION_CONFLICT') {
        await qc.invalidateQueries({ queryKey: ['plans'] });
        setFormError(
          'Someone else changed this plan while you were editing. Close and reopen it to see the latest values.',
        );
      } else if (!applyFieldErrors(e, setError, FIELDS)) {
        setFormError(errorMessage(e));
      }
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {editing ? `Edit ${plan.name}` : 'New plan'}
        <Typography variant="body2" color="text.secondary">
          {editing
            ? 'Changes apply to future extensions; existing paid periods are not touched.'
            : 'Plans set the default duration and price used when extending a subscription.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              autoFocus
              fullWidth
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message ?? 'Shown to admins, e.g. “Standard monthly”'}
            />
            <TextField
              label="Code"
              fullWidth
              disabled={editing}
              {...register('code')}
              error={Boolean(errors.code)}
              helperText={errors.code?.message ?? (editing ? 'Fixed once created' : 'e.g. monthly')}
              slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: 'none' } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              control={control}
              name="interval"
              render={({ field }) => (
                <TextField
                  select
                  label="Billing interval"
                  fullWidth
                  disabled={editing}
                  {...field}
                  onChange={(e) => {
                    const next = e.target.value as PlanInterval;
                    field.onChange(next);
                    if (!dirtyFields.durationDays || getValues('durationDays') === '')
                      setValue('durationDays', String(DEFAULT_DAYS[next]));
                  }}
                  helperText={editing ? 'Fixed once created' : ' '}
                >
                  {(Object.keys(INTERVAL_LABELS) as PlanInterval[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {INTERVAL_LABELS[k]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField
              label="Duration"
              fullWidth
              {...register('durationDays')}
              error={Boolean(errors.durationDays)}
              helperText={errors.durationDays?.message ?? 'Days added per extension'}
              slotProps={{
                htmlInput: { inputMode: 'numeric' },
                input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
              }}
            />
          </Stack>
          <TextField
            label="Users per shop"
            fullWidth
            {...register('maxUsers')}
            error={Boolean(errors.maxUsers)}
            helperText={
              errors.maxUsers?.message ??
              'Active users plus pending invitations a shop on this plan may have. Empty = unlimited.'
            }
            slotProps={{
              htmlInput: { inputMode: 'numeric' },
              input: { endAdornment: <InputAdornment position="end">users</InputAdornment> },
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Price"
              fullWidth
              {...register('price')}
              error={Boolean(errors.price)}
              helperText={errors.price?.message ?? 'Leave empty for no list price'}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              label="Currency"
              fullWidth
              disabled={editing}
              {...register('currency', {
                setValueAs: (v: string) => v.toUpperCase(),
              })}
              error={Boolean(errors.currency)}
              helperText={errors.currency?.message ?? (editing ? 'Fixed once created' : 'ISO code')}
              slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
            />
          </Stack>
          {editing && (
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(_, checked) => field.onChange(checked)}
                    />
                  }
                  label={
                    <span>
                      Active
                      <Typography variant="caption" color="text.secondary" component="div">
                        Inactive plans can’t be picked for new extensions.
                      </Typography>
                    </span>
                  }
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" loading={isSubmitting}>
          {editing ? 'Save changes' : 'Create plan'}
        </Button>
      </DialogActions>
    </form>
  );
}
